"use client";
import { ReactElement, ReactNode, useMemo } from "react";
import { Alert, AlertColor, useMediaQuery, useTheme } from "@mui/material";
import Snackbar, { SnackbarOrigin } from "@mui/material/Snackbar";
import CheckedIcon from "../components/icons/checkedIcon";
import { RegularText } from "@/common/texts";
import CloseIcon from "@/components/icons/closeIcon";

import {
  ERROR_COLOR,
  ERROR_VARIANT_COLOR,
  INPUT_BORDER_COLOR,
  NEUTRO_TITLES_COLOR,
  PRIMARY_BRAND_VARIANT_COLOR,
  PRIMARY_BUTTON_COLOR,
  WARNING_COLOR,
} from "@/utils/constants";

export interface State extends SnackbarOrigin {
  open: boolean;
  autoHideDuration: number | null;
  icon: ReactNode | null;
}

interface GenericSnackbarParams {
  message: string | ReactElement;
  severity: AlertColor;
  state: State;
  setState: React.Dispatch<React.SetStateAction<State>>;
  actionElement?: ReactElement;
}

export const GenericSnackbar = (params: GenericSnackbarParams) => {
  const { vertical, horizontal, open, autoHideDuration, icon } = params.state;

  const theme = useTheme();
  const isSmallMedia = useMediaQuery(theme.breakpoints.down("sm"));

  const handleClose = () => {
    params.setState({ ...params.state, open: false });
  };

  const severityColors = useMemo(() => {
    switch (params.severity) {
      case "error":
        return {
          border: ERROR_COLOR,
          background: ERROR_VARIANT_COLOR,
          color: NEUTRO_TITLES_COLOR,
        };
      case "warning":
        return {
          border: WARNING_COLOR,
          background: WARNING_COLOR,
          color: NEUTRO_TITLES_COLOR,
        };
      case "info":
        return {
          border: INPUT_BORDER_COLOR,
          background: WARNING_COLOR,
          color: NEUTRO_TITLES_COLOR,
        };
      case "success":
        return {
          border: PRIMARY_BUTTON_COLOR,
          background: PRIMARY_BRAND_VARIANT_COLOR,
          color: NEUTRO_TITLES_COLOR,
        };
    }
  }, [params.severity]);

  const iconSeverity = useMemo<ReactNode>(() => {
    if (icon) return icon;
    switch (params.severity) {
      case "error":
        return <CloseIcon color={ERROR_COLOR} />;
      case "warning":
        //Todo replace icons when its available
        return <CheckedIcon />;
      case "info":
        //Todo replace icons when its available
        return <CheckedIcon />;
      case "success":
        //Todo replace icons when its available
        return <CheckedIcon />;
      default:
        return null;
    }
  }, [icon, params.severity]);
  // {
  //   console.log(severityColors);
  // }

  return (
    <Snackbar
      anchorOrigin={{ vertical, horizontal }}
      open={open}
      onClose={handleClose}
      key={vertical + horizontal}
      autoHideDuration={autoHideDuration}
      sx={{
        zIndex: 9999999,
      }}
      className={isSmallMedia ? "safe-area" : ""}
    >
      <Alert
        severity={params.severity}
        icon={iconSeverity}
        sx={{
          border: `1px solid ${severityColors.border}`,
          boxShadow: "0 0 10px 1px rgba(172, 172, 172, 0.5)",
          borderRadius: "8px",
          backgroundColor: `${severityColors.background == "#FC672F" ? "#F4C1B8" : severityColors.background} !important`,
          minWidth: "330px",
          "& .MuiAlert-message": {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "nowrap",
            gap: "24px",
          },
        }}
      >
        <RegularText fontSize={"18px"} color={severityColors.color}>
          {params.message}{" "}
        </RegularText>
        {params.actionElement}
      </Alert>
    </Snackbar>
  );
};
